import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IName } from 'interfaces/common/common';

import {
	Datepicker,
	Input,
	Modal,
	Select,
	SpinnerOverlay,
	SubmissionButtons,
	TextArea,
} from '@components/common';

import useMutateConfigurationRunTemplatesConfigTree from '@hooks/cmModule/configurationTemplates/useMutateConfigurationRunTemplatesConfigTree';
import useMutateConfigurationSaveTemplatesTree from '@hooks/cmModule/configurationTemplates/useMutateConfigurationSaveTemplatesTree';
import useMutateConfigurationTemplatesConfigTree from '@hooks/cmModule/configurationTemplates/useMutateConfigurationTemplatesConfigTree';
import useMutateConfigurationTemplateTreeByPath from '@hooks/cmModule/configurationTemplates/useMutateConfigurationTemplateTreeByPath';
import useMutateConfigurationTemplateUpdateConfigTree from '@hooks/cmModule/configurationTemplates/useMutateConfigurationTemplateUpdateConfigTree';
import useMutateConfigurationTemplateUpdateName from '@hooks/cmModule/configurationTemplates/useMutateConfigurationTemplateUpdateName';
import useMutateConfigurationTemplateUpdateNotes from '@hooks/cmModule/configurationTemplates/useMutateConfigurationTemplateUpdateNotes';
import useQueryConfigurationCheckTemplatesConfig from '@hooks/cmModule/configurationTemplates/useQueryConfigurationCheckTemplatesConfig';
import useQueryConfigurationTemplate from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplate';
import useQueryConfigurationTemplatesConfigDates from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplatesConfigDates';
import useQueryConfigurationTemplatesConfigTimestamps from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplatesConfigTimestamps';
import useQueryConfigurationTemplatesConfigTypes from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplatesConfigTypes';
import useQueryConfigurationTemplatesNetworkElements from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplatesNetworkElements';

import { isNotEmpty } from '@utils/common';
import { formatDate } from '@utils/date';
import { createMoTreeFromFlatMavenirTree } from '@utils/treeFormatter/treeFormatter';
import { convertXMLToFlatTreeData } from '@components/common/FlatXMLTree/utils';
import { useModal } from '@components/common/Modal/utils';

import { IConfigurationTemplate, IDumpIdTreeParsed } from '@interfaces/cmModule/configurationTemplates';
import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { TMapTree } from '@components/common/FlatXMLTree/interface';
import { IConfigurationTemplateForm, ISubmitTemplateTree } from './interfaces';

import { EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_1000 } from '@constants/modals';
import { regExpLettersAndUnderscore } from '@constants/regExp';
import { MAX_50_LENGTH, MAX_500_LENGTH } from '@constants/validationMessages';
import { clonedTemplateNameError, EConfigurationTemplateValues } from './constants';
import { NON_EDITABLE_CONFIGURATION_IDS } from '@components/routes/AdminPanel/ManageNetworkElementConfigurations/constants';

import { convertMoTreeToXmlData, convertXmlDataToMoTree, getPathForArrayObject, getSkippedXmlNodes } from '../utils';

import FlatTreeMavenir from '../../ConfigurationComparison/MOTreeMavenir/FlatTreeMavenir';
import ConfigurationTemplateTreeModal from './ConfigurationTemplateTreeModal';
import { getSchema } from './schema';
import { useNameValidation } from './useNameValidation';
import { extractTemplateTime, filterAndCreateMoTree, sortTreeNodesWithParametersFirst } from './utils';

import styles from './ConfigurationTemplateForm.module.scss';

interface IConfigurationTemplateFormProps {
	isEditMode?: boolean;
	isCloneMode?: boolean;
	template?: IConfigurationTemplate;
	checkTemplateNameError?: string;
	closeTemplateModal: () => void;
	setIsDirtyForm: (isDirty: boolean) => void;
	checkTemplateName: (data: IName) => Promise<boolean>;
}

const ConfigurationTemplateForm = ({
	isEditMode,
	isCloneMode,
	template,
	checkTemplateNameError,
	closeTemplateModal,
	setIsDirtyForm,
	checkTemplateName,
}: IConfigurationTemplateFormProps) => {
	const { onClose, setTitleValue } = useModal();

	const {
		id,
		name: defaultName,
		notes: defaultNotes,
		configType: defaultConfigType,
		dataSourceCreatedAtText: defaultDataSourceCreatedAtText,
		dataSourceCreatedAt,
	} = template || {};

	const defaultSourceAtOption = defaultDataSourceCreatedAtText && defaultConfigType
		? {
			label: extractTemplateTime(defaultDataSourceCreatedAtText),
			value: dataSourceCreatedAt,
		}
		: null;

	const defaultConfigTypeOption = defaultConfigType
		? {
			label: defaultConfigType.name,
			value: defaultConfigType.title,
		}
		: null;

	const { validateName } = useNameValidation(template?.name, checkTemplateName);

	const schema = useMemo(() => {
		return getSchema(
			validateName,
			isCloneMode ? clonedTemplateNameError : checkTemplateNameError,
			defaultName,
			isCloneMode,
		);
	}, [
		validateName,
		checkTemplateNameError,
		defaultName,
		isCloneMode,
	]);

	const [treeTemplateData, setTreeTemplateData] = useState<IFlatMoTreeNode[]>([]);
	const [treeData, setTreeData] = useState<TMapTree>(new Map());

	const [isOpenTreeTemplateModal, setIsOpenTreeTemplateModal] = useState(false);
	const [isOpenXmlTreeModal, setIsOpenXmlTreeModal] = useState(false);
	const [isDirtyTemplateTreeForm, setIsDirtyTemplateTreeForm] = useState(true);
	const [isDirtyXMLTreeForm, setIsDirtyXMLTreeForm] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, dirtyFields },
		setError,
		clearErrors,
		control,
		setValue,
		resetField,
		watch,
	} = useForm<IConfigurationTemplateForm>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema),
		defaultValues: {
			[EConfigurationTemplateValues.name]: defaultName || '',
			[EConfigurationTemplateValues.notes]: !isCloneMode ? defaultNotes || '' : '',
			[EConfigurationTemplateValues.type]: defaultConfigTypeOption,
			[EConfigurationTemplateValues.networkElement]: null,
			[EConfigurationTemplateValues.date]: dataSourceCreatedAt || null,
			[EConfigurationTemplateValues.time]: defaultSourceAtOption,
			[EConfigurationTemplateValues.validationRulesFolderId]: template?.validationRulesFolderId,
			[EConfigurationTemplateValues.validationRulesFolderName]: template?.validationRulesFolderName,
		},
	});

	const configType = watch(EConfigurationTemplateValues.type)?.value;
	const networkElement = watch(EConfigurationTemplateValues.networkElement)?.value;
	const date = watch(EConfigurationTemplateValues.date);
	const time = watch(EConfigurationTemplateValues.time)?.value;
	const timeLabelText = watch(EConfigurationTemplateValues.time)?.label;
	const name = watch(EConfigurationTemplateValues.name);
	const notes = watch(EConfigurationTemplateValues.notes);
	const validationRulesFolderId = watch(EConfigurationTemplateValues.validationRulesFolderId);
	const validationRulesFolderName = watch(EConfigurationTemplateValues.validationRulesFolderName);

	const selectedDate = useMemo(() =>  date ? new Date(date) : null, [date]);
	const dataSourceCreatedAtText = date && time ? `${formatDate(date)} ${timeLabelText}` : '';

	const isTimeAvailable = !!watch(EConfigurationTemplateValues.date);
	const isClonedNameSame = isCloneMode && name === defaultName;
	const isEditOrCloneMode = isEditMode || isCloneMode;
	const hasNoDirtyFields = !!Object.keys(dirtyFields).length;

	const openTemplateTreeModal = useCallback(() => {
		setIsOpenTreeTemplateModal(true);
	}, [setIsOpenTreeTemplateModal]);

	const closeTemplateTreeModal = useCallback(() => {
		setIsOpenTreeTemplateModal(false);
		closeTemplateModal();
		setIsDirtyTemplateTreeForm(false);
	}, [setIsOpenTreeTemplateModal, closeTemplateModal]);

	const openXmlTreeModal = useCallback(() => {
		setIsOpenXmlTreeModal(true);
	}, [setIsOpenXmlTreeModal]);

	const closeXmlTreeModal = useCallback(() => {
		setIsOpenXmlTreeModal(false);
		setIsDirtyXMLTreeForm(false);
		closeTemplateModal();
	}, [setIsOpenXmlTreeModal, closeTemplateModal]);

	const updateTemplateData = useCallback((data: IFlatMoTreeNode[], skippedParameters: string[]) => {
		const sortedData = sortTreeNodesWithParametersFirst(data || []);

		if (isEditOrCloneMode && sortedData) {
			const moTree = createMoTreeFromFlatMavenirTree(sortedData);

			if (!moTree) {
				return;
			}

			const flatTreeXmlData = convertXMLToFlatTreeData(
				convertMoTreeToXmlData(moTree),
				NON_EDITABLE_CONFIGURATION_IDS,
				skippedParameters,
			);

			setTreeData(flatTreeXmlData);
		} else {
			openTemplateTreeModal();
			setTreeTemplateData(sortedData);
		}
	}, [
		isEditOrCloneMode,
		openTemplateTreeModal,
	]);

	const onSuccessTreeParsed = ({ isTreeParsed, dumpId }: IDumpIdTreeParsed) => {
		if (!dumpId || !isTreeParsed){
			return;
		}

		generateTree({
			dumpId,
			path: '',
		});
	};

	const handleChangeData = useCallback((date: number | null) => {
		setValue(EConfigurationTemplateValues.date, date);
	}, [setValue]);

	const getCheckboxLabel = useCallback((value: boolean) => (value ? 'Unskipped' : 'Skipped'), []);

	const {
		configTypes,
		isLoadingConfigTypes,
	} = useQueryConfigurationTemplatesConfigTypes();

	const {
		networkElements,
		isLoadingNetworkElements,
	} = useQueryConfigurationTemplatesNetworkElements({
		configType: configType,
	});

	const {
		configDates,
		isLoadingConfigDates,
	} = useQueryConfigurationTemplatesConfigDates({
		configType: configType,
		networkElementId: networkElement,
	});

	const {
		configTimestamps,
		isLoadingConfigTimestamps,
	} = useQueryConfigurationTemplatesConfigTimestamps({
		configType: configType,
		networkElementId: networkElement,
		configDate: date ? formatDate(date) : '',
	});

	const {
		configurationTemplate,
		configTreeTemplate,
		isLoadingConfigTemplate,
	} = useQueryConfigurationTemplate({ id, onSuccess: updateTemplateData });

	const onSaveTemplateSuccess = () => {
		openXmlTreeModal();
	};

	const {
		generatedTree,
		generateTree,
		isLoadingConfigTree,
	} = useMutateConfigurationTemplatesConfigTree({ onSuccess: updateTemplateData });

	const {
		getConfigForPath,
		isLoadingGetConfigForPath,
	} = useMutateConfigurationTemplateTreeByPath();

	const {
		runTree,
		isLoadingRunConfigTree,
		dumpId,
	} = useMutateConfigurationRunTemplatesConfigTree();

	const {
		updateName,
		isLoadingUpdateName,
	} = useMutateConfigurationTemplateUpdateName();

	const {
		updateNotes,
		isLoadingUpdateNotes,
	} = useMutateConfigurationTemplateUpdateNotes();

	const {
		savedTemplateTree,
		saveTemplateTree,
		isLoadingSaveTemplatesTree,
	} = useMutateConfigurationSaveTemplatesTree({ onSuccess: onSaveTemplateSuccess });

	const {
		isLoadingConfigParsed,
	} = useQueryConfigurationCheckTemplatesConfig({ dumpId, onSuccess: onSuccessTreeParsed });

	const {
		updateTree,
		isLoadingUpdateConfigTree,
	} = useMutateConfigurationTemplateUpdateConfigTree({
		onSuccess: closeTemplateModal,
	});

	const handleSetTemplateTree = useCallback(async () => {
		const sortedData = sortTreeNodesWithParametersFirst(treeTemplateData);
		const moTree = filterAndCreateMoTree(sortedData, true);

		const moTreeWithFixedPath = filterAndCreateMoTree(
			sortedData.map(node => ({ ...node, keysPath: getPathForArrayObject(node)! })),
			true,
		);

		if (!moTree || !moTreeWithFixedPath) {
			return;
		}

		const xmlMap = convertXMLToFlatTreeData(
			convertMoTreeToXmlData(moTree),
			NON_EDITABLE_CONFIGURATION_IDS,
			configurationTemplate?.skippedParameters || [],
		);

		setTreeData(xmlMap);

		if (!isEditOrCloneMode) {
			await saveTemplateTree({
				dataSourceCreatedAtText,
				configType: configType!,
				networkElementId: networkElement!,
				tree: moTreeWithFixedPath,
				notes: notes || '',
				name: name,
				validationRulesFolderId: validationRulesFolderId ? validationRulesFolderId : undefined,
				validationRulesFolderName: validationRulesFolderName ? validationRulesFolderName : undefined,
			});
		}
	}, [
		configurationTemplate,
		configType,
		networkElement,
		dataSourceCreatedAtText,
		name,
		notes,
		treeTemplateData,
		isEditOrCloneMode,
		validationRulesFolderId,
		validationRulesFolderName,
		saveTemplateTree,
	]);

	const handleSubmitMOTree = useCallback((values: IConfigurationTemplateForm) => {
		const {
			networkElement,
			time,
			type,
		} = values;

		runTree({
			networkElementId: networkElement?.value || '',
			configDate: time?.value || 0,
			configType: type?.value || '',
		});
	}, [runTree]);

	const updateConfigTemplate = useCallback(async () => {
		if (!id) {
			return;
		}

		if (name !== defaultName) {
			await updateName({ id, name });
		}

		if (!!notes && notes !== defaultNotes) {
			await updateNotes({ id, notes });
		}
	}, [
		id,
		name,
		defaultName,
		notes,
		defaultNotes,
		updateName,
		updateNotes,
	]);

	const cloneHandler = useCallback(async () => {
		if (isCloneMode && configurationTemplate) {
			const moTree = createMoTreeFromFlatMavenirTree(configTreeTemplate);

			if (!moTree) {
				return;
			}

			await saveTemplateTree({
					name,
					notes,
					dataSourceCreatedAtText: configurationTemplate!.dataSourceCreatedAtText,
					configType: configurationTemplate!.configType?.name,
					networkElementId: configurationTemplate!.networkElementId,
					tree: moTree,
					validationRulesFolderId: validationRulesFolderId ? validationRulesFolderId : undefined,
					validationRulesFolderName: validationRulesFolderName ? validationRulesFolderName : undefined,
				});
			}
	}, [
		isCloneMode,
		configurationTemplate,
		name,
		notes,
		configTreeTemplate,
		validationRulesFolderId,
		validationRulesFolderName,
		saveTemplateTree,
	]);

	const proceedHandler = useCallback(async () => {
		if (isEditMode){
			await updateConfigTemplate();
		} else if (isCloneMode) {
			await cloneHandler();
		}

		openXmlTreeModal();
	}, [
		isEditMode,
		isCloneMode,
		cloneHandler,
		updateConfigTemplate,
		openXmlTreeModal,
	]);

	const positiveHandler = useCallback(async () => {
		if (isEditMode) {
			await updateConfigTemplate();

			closeTemplateModal();
		} else if (isCloneMode) {
			await cloneHandler();

			closeTemplateModal();
		} else {
			handleSubmit(handleSubmitMOTree)();
			setIsDirtyForm(false);
		}
	}, [
		isEditMode,
		isCloneMode,
		handleSubmit,
		handleSubmitMOTree,
		updateConfigTemplate,
		cloneHandler,
		setIsDirtyForm,
		closeTemplateModal,
	]);

	const handleSubmitXMLTree = useCallback(({
		tree,
		folderId,
		folderName,
	}: ISubmitTemplateTree) => {
		if (isEditOrCloneMode && !id || !isEditOrCloneMode && !savedTemplateTree?.id) {
			return;
		}

		const templateId = !isEditOrCloneMode ? savedTemplateTree?.id : id;

		updateTree({
			id: templateId!,
			tree: convertXmlDataToMoTree(tree),
			skippedParameters: getSkippedXmlNodes(tree),
			validationRulesFolderId: folderId,
			validationRulesFolderName: folderName,
		});
	}, [
		id,
		savedTemplateTree?.id,
		isEditOrCloneMode,
		updateTree,
	]);

	const { networkElementId } = configurationTemplate;

	useEffect(() => {
		setIsDirtyForm(hasNoDirtyFields);
	}, [setIsDirtyForm, hasNoDirtyFields]);

	useEffect(() => {
		if (!isEditMode && !isCloneMode) {
			setTitleValue('Add new template');
		}

		if (isEditMode) {
			setTitleValue('Edit template');
		}

		if (isCloneMode) {
			setTitleValue('Clone template');
		}
	}, [setTitleValue, isEditMode, isCloneMode]);

	useEffect(() => {
		setValue(EConfigurationTemplateValues.date, null);
		resetField(EConfigurationTemplateValues.time);
		resetField(EConfigurationTemplateValues.networkElement);
	}, [configType, resetField, setValue]);

	useEffect(() => {
		resetField(EConfigurationTemplateValues.date);
		resetField(EConfigurationTemplateValues.time);
	}, [networkElement, resetField]);

	useEffect(() => {
		resetField(EConfigurationTemplateValues.time);
	}, [date, resetField]);

	useEffect(() => {
		if (dataSourceCreatedAt && isEditOrCloneMode) {
			setValue(EConfigurationTemplateValues.date, dataSourceCreatedAt || null);
			setIsDirtyForm(false);
		}
	}, [
		dataSourceCreatedAt,
		isEditOrCloneMode,
		setValue,
		setIsDirtyForm,
	]);

	useEffect(() => {
		if (isEditOrCloneMode && networkElementId) {
			const foundNetworkElement = networkElements
				.find((el) => el?.value === networkElementId) || null;

			setValue(EConfigurationTemplateValues.networkElement, foundNetworkElement);
			setIsDirtyForm(false);
		}
	}, [
		isEditOrCloneMode,
		networkElementId,
		networkElements,
		setIsDirtyForm,
		setValue,
	]);

	useEffect(() => {
		if (checkTemplateNameError) {
			setError(EConfigurationTemplateValues.name, {
				type: 'custom',
				message: checkTemplateNameError,
			});
		}
	}, [
		checkTemplateNameError,
		setError,
		clearErrors,
	]);

	useEffect(() => {
		if (isClonedNameSame) {
			setError(EConfigurationTemplateValues.name, {
				type: 'custom',
				message: clonedTemplateNameError,
			});
		}
	}, [
		isClonedNameSame,
		setError,
		clearErrors,
	]);

	useEffect(() => {
		if (treeData.size === 0 && configTreeTemplate.length > 0) {
			updateTemplateData(configTreeTemplate || [], configurationTemplate?.skippedParameters || []);
		}
	}, [
		treeData,
		configTreeTemplate,
		configurationTemplate?.skippedParameters,
		updateTemplateData,
	]);

	const defaultMavenirTreeData = useMemo(() => {
		return generatedTree;
		// eslint-disable-next-line
	}, [generatedTree?.length > 0]);

	const isNotEmptyErrors = isNotEmpty(errors);

	const isSubmitDisabled = !isEditOrCloneMode
		? !configType || !networkElement || !date || !time || !name
		: notes === defaultNotes && name === defaultName;

	const isLoading = isLoadingConfigTypes
		|| isLoadingNetworkElements
		|| isLoadingConfigDates
		|| isLoadingConfigTimestamps
		|| isLoadingRunConfigTree
		|| isLoadingConfigTree
		|| isLoadingConfigParsed
		|| isLoadingSaveTemplatesTree
		|| isLoadingUpdateName
		|| isLoadingUpdateNotes
		|| isLoadingConfigTemplate
		|| isLoadingUpdateConfigTree
		|| isLoadingGetConfigForPath
		|| isLoadingConfigTree;

	return (
		<div className={styles.wrapper}>
			{isLoading && <SpinnerOverlay />}
			<Modal
				height='80%'
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyTemplateTreeForm}
				isOpen={isOpenTreeTemplateModal}
				onClose={closeTemplateTreeModal}
				title='Configuration template tree'
				width={MODAL_WIDTH_1000}
			>
				<FlatTreeMavenir
					cancelHandler={closeTemplateTreeModal}
					cancelText='Close'
					data={treeTemplateData}
					defaultData={defaultMavenirTreeData}
					getCheckboxLabel={getCheckboxLabel}
					isCloseWithConfirmation
					isLoading={isLoading}
					isReadOnly={false}
					isRevertReset
					isSingleSelectMode
					isWithReset
					setData={setTreeTemplateData}
					setIsDirtyForm={setIsDirtyTemplateTreeForm}
					submitHandler={handleSetTemplateTree}
					submitText='Apply tree'
					treeId={dumpId}
					updateConfig={getConfigForPath}
				/>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyXMLTreeForm}
				isOpen={isOpenXmlTreeModal}
				onClose={closeXmlTreeModal}
				title='Edit configuration template'
				width={MODAL_WIDTH_1000}
			>
				<ConfigurationTemplateTreeModal
					closeViewModal={closeXmlTreeModal}
					data={treeData}
					onSubmit={handleSubmitXMLTree}
					setIsDirtyForm={setIsDirtyXMLTreeForm}
					template={configurationTemplate}
				/>
			</Modal>
			<Input
				key='template-name-input'
				errorMessage={errors?.name?.message}
				isMaxLengthCounter
				label='Template Name'
				maxLength={MAX_50_LENGTH}
				name={EConfigurationTemplateValues.name}
				placeholder='Enter template name'
				regexValidation={regExpLettersAndUnderscore}
				register={register}
				required
				valueLength={name?.length}
			/>
			<div className={styles.separator}/>
			<div className={styles.title}>Template Source</div>
			<div className={styles.sourceWrapper}>
				<Select
					control={control}
					disabled={isLoadingConfigTypes || isEditOrCloneMode}
					errorMessage={errors?.type?.message}
					label='Configuration Type'
					name={EConfigurationTemplateValues.type}
					options={isEditOrCloneMode ? [defaultConfigTypeOption] : configTypes}
					placeholder='Choose type'
					required
				/>
				<Select
					control={control}
					disabled={isLoadingNetworkElements || !configType || isEditOrCloneMode}
					errorMessage={errors?.networkElement?.message}
					label='Network Element'
					name={EConfigurationTemplateValues.networkElement}
					options={networkElements}
					placeholder='Choose network element'
					required
				/>
			</div>
			<div className={styles.dateWrapper}>
				<Datepicker
					disableInput
					disabled={isLoadingConfigDates || !networkElement || isEditOrCloneMode}
					errorMessage={errors?.date?.message}
					handleChange={handleChangeData}
					includeDates={configDates}
					isMobileDevice={false}
					isTodayButton={false}
					label='Date'
					name={EConfigurationTemplateValues.date}
					placeholderText='Choose date'
					required
					selected={selectedDate}
				/>
				<Select
					control={control}
					disabled={!isTimeAvailable || isLoadingConfigTimestamps || isEditOrCloneMode}
					errorMessage={errors?.time?.message}
					label='Time'
					name={EConfigurationTemplateValues.time}
					options={configTimestamps}
					placeholder='Choose time'
					required
				/>
				{dataSourceCreatedAtText && (
					<div className={styles.configurationDateWrapper}>
						<span>Configuration used:</span>
						<span className={styles.configurationDate}>{dataSourceCreatedAtText}</span>
					</div>
				)}
			</div>
			<div className={styles.separator}/>
			<TextArea
				errorMessage={errors?.notes?.message}
				isMaxLengthCounter
				isNotResizable
				label='Notes'
				maxLength={MAX_500_LENGTH}
				name={EConfigurationTemplateValues.notes}
				placeholder='Enter notes'
				register={register}
				withUseForms
			/>
			<SubmissionButtons
				className={styles.btnWrapper}
				disabledPositive={isSubmitDisabled || isNotEmptyErrors || isClonedNameSame}
				disabledProceed={isNotEmptyErrors || isClonedNameSame}
				isProceedButtonNeeded={isEditOrCloneMode}
				negativeHandler={onClose}
				positiveHandler={positiveHandler}
				positiveLabel={isEditOrCloneMode ? 'Save and Close' : 'Build config template tree'}
				proceedHandler={proceedHandler}
				proceedLabel={!isEditMode ? 'Save and Edit' : 'Next: Configuration'}
			/>
		</div>
	);
};

export default ConfigurationTemplateForm;
